import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Observable, map, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiRoot } from '../../shared/constants/api-endpoints';

@Injectable({ providedIn: 'root' })
export class AttachmentService {
  private apiUrl = ""

  // This method is used to initialize the class instance using http
  constructor(private http: HttpClient) {
  }

  // This method is used to get url
  getURL() {
    const mode = (environment as any).apiMode || 'gateway';
    this.apiUrl = environment.api_gateway;
    if (mode === 'siebel') {
      this.apiUrl = environment.api_siebel
    }
    if (mode === 'soa') {
      this.apiUrl = environment.api_soa_dev
    }
    return this.apiUrl;
  }

  // This method is used to get request using root path, end point, and query params
  getRequest(rootPath: string, endPoint: string, queryParams: { [key: string]: any }) {
    let params = new HttpParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params = params.set(key, String(value));
      }
    });
    const fullPath = `${this.getURL()}${rootPath}/${endPoint}`;
    return this.http.get(fullPath, { params });
  }

  // This method is used to upload file using root path, end point, file, and metadata
  uploadFile(
    rootPath: string,
    endPoint: string,
    file: File,
    metadata: Omit<
      Record<
        | 'documentTypeNameEn'
        | 'folderId'
        | 'folderParentId'
        | 'folderParentType'
        | 'serviceType'
        | 'validation'
        | 'notes'
        | 'mot'
        | 'svsSubject',
        string | number
      >,
      never
    >
  ): Observable<{ progress: number; response?: any }> {
    const fullPath = `${this.getURL()}${rootPath}/${endPoint}`;

    const formData = new FormData();
    formData.append('file', file);

    // Append each metadata field to FormData
    Object.entries(metadata).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    // Optional: include file extension if backend expects it
    formData.append('fileExt', file.name.split('.').pop() ?? 'bin');

    return this.http.post(fullPath, formData, {
      reportProgress: true,
      observe: 'events',
    }).pipe(
      map((event: HttpEvent<any>) => {
        if (event.type === HttpEventType.UploadProgress) {
          // Use server-reported total if present; otherwise fall back to file.size
          const total = (event as any).total ?? file.size;
          if (total && total < 100) {
            return { progress: Math.round((100 * event.loaded) / total) };
          }
          // If total is still unavailable, emit a minimal bump to show activity
          return { progress: event.loaded ? Math.min(99, Math.max(1, Math.round((event.loaded / (file.size || 1)) * 100))) : 1 };
        }
        if (event.type === HttpEventType.Response) {
          return { progress: 100, response: event.body };
        }
        return { progress: 0 };
      })
    );
  }


  // This method is used to delete request using root path, end point, and query params
  deleteRequest(rootPath: string, endPoint: string, queryParams: { [key: string]: any }) {
    let params = new HttpParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params = params.set(key, String(value));
      }
    });
    const fullPath = `${this.getURL()}${rootPath}/${endPoint}`;
    return this.http.delete(fullPath, { params });
  }

  // This method is used to download file using document id, folder id, and validation
  downloadFile(documentId: string, folderId: string, validation: string): Observable<Blob> {
    const url = `${this.getURL()}${ApiRoot.JUPITER_REST}/download`;
    const params = new HttpParams()
      .set('documentId', documentId)
      .set('folderId', folderId)
      .set('validation', validation);

    return this.http.get(url, {
      params,
      responseType: 'blob', // <-- important: tells Angular to expect binary data
    });
  }

  // This method is used to delete bulk documents using root path, end point, and payload
  deleteBulkDocuments(rootPath: string, endPoint: string, payload: any) {
    let params = new HttpParams();
    const fullPath = `${this.getURL()}${rootPath}/${endPoint}`;
    return this.http.post(fullPath, payload);
  }
}
